import Highcharts from 'highcharts'

import { PaletteMode, Theme } from '@mui/material'

type StyleOverrideRoot = { theme: Theme }

const ACTION_CELL_WIDTH = 80
export const LEFT_COLUMN_WIDTH = 160
export const RIGHT_COLUMN_WIDTH = 300

const GraphColors = {
  power: {
    lighter: '#D2D2D2',
    light: '#AEAEAE',
    main: '#8A8A8A',
    dark: '#565656',
    darker: '#303030',
    contrastText: '#ffffff',
  },
  bt: {
    lighter: '#F0C2CD',
    light: '#D6748B',
    main: '#B13654',
    dark: '#901533',
    darker: '#640119',
    contrastText: '#ffffff',
  },
  et: {
    lighter: '#CCE1F4',
    light: '#82B1DB',
    main: '#4780B5',
    dark: '#2264A1',
    darker: '#003260',
    contrastText: '#ffffff',
  },
  ror: {
    lighter: '#CBE4E4',
    light: '#78C5C5',
    main: '#3A8585',
    dark: '#076969',
    darker: '#076969',
    contrastText: '#ffffff',
  },
  bbp: {
    lighter: '#F3E8C2',
    light: '#D7C895',
    main: '#D2BC6D',
    dark: '#907820',
    darker: '#5F4B05',
    contrastText: '#1e1e1e',
  },
  inletTemp: {
    lighter: '#21bb81',
    light: '#1dad77',
    main: '#188b60',
    dark: '#126a49',
    darker: '#0d4c35',
    contrastText: '#ffffff',
  },
  drumTemp: {
    lighter: '#a14ba1',
    light: '#8f438f',
    main: '#773877',
    dark: '#5a2b5a',
    darker: '#401f40',
    contrastText: '#ffffff',
  },
  drumPressure: {
    lighter: '#eb86eb',
    light: '#d36fd3',
    main: '#bb59bb',
    dark: '#a143a1',
    darker: '#873387',
    contrastText: '#ffffff',
  },
  rpm: {
    lighter: '#C6CAEE',
    light: '#99A2F3',
    main: '#717BD9',
    dark: '#4550B5',
    darker: '#2D3370',
    contrastText: '#ffffff',
  },
  fan: {
    lighter: '#B3E6E9',
    light: '#85E1E7',
    main: '#5EDDE5',
    dark: '#2ABBC3',
    darker: '#008992',
    contrastText: '#1e1e1e',
  },
  cracks: {
    lighter: '#f77c5a',
    light: '#e56340',
    main: '#E7522A',
    dark: '#c73f1b',
    darker: '#af300e',
    contrastText: '#1e1e1e',
  },
  target: {
    main: '#D2BC6D',
  },
  plotline: {
    light: '#E0E0E3',
    main: '#A0A0A3',
    dark: '#565656',
    darker: '#303030',
  },
}

const commonColors = {
  navbarHeader: {
    main: '#3A0B2B',
  },
  error: {
    light: '#FF1A1A',
    main: '#DF0214',
    dark: '#B10000',
    contrastText: '#FFFFFF',
  },
}

const DarkTheme = {
  mode: 'dark' as PaletteMode,
  ...GraphColors,
  ...commonColors,
  tableHeader: {
    main: '#616161',
  },
  qrcode: {
    light: '#dddddd',
    main: '',
    dark: '#1f1f1f',
  },
  background: {
    default: '#1f1f1f',
  },
  primary: {
    lighter: '#565656',
    light: '#BAB1B7',
    main: '#A2979F', // button main
    dark: '#806B79', // button hover
    contrastText: '#1e1e1e',
  },
  secondary: {
    light: '#2B2B2B',
    main: '#1f1f1f',
    dark: '#303030',
    contrastText: '#FFFFFF',
  },
  dryingStage: {
    main: '#455532',
  },
  maillardStage: {
    main: '#736517',
  },
}

const LightTheme = {
  mode: 'light' as PaletteMode,
  ...GraphColors,
  ...commonColors,
  background: {
    default: '#FFFFFF',
  },
  qrcode: {
    light: '#ffffff',
    main: '',
    dark: '#000000',
  },
  primary: {
    lighter: '#F2F0F2', // button hover background
    light: '#A2979F', // button idle
    main: '#3A0B2B', // button main border
    dark: '#561B44', // button hover border
    contrastText: '#FFFDFC',
  },
  secondary: {
    light: '#F4E6DD',
    main: '#E3C9A7',
    dark: '#D0B082',
    contrastText: '#24021A',
  },
  dryingStage: {
    main: '#c9d7b2',
  },
  maillardStage: {
    main: '#f7e797',
  },
}

const getStyleOverrides = (isDarkTheme: boolean) => {
  return {
    components: {
      MuiTable: {
        styleOverrides: {
          root: ({ theme }: StyleOverrideRoot) => ({
            //Cupping, Log, Profiles, Roast Schedule Tables
            '&.cupping-score-table td, &.cupping-score-table th': {
              padding: '8px 4px',

              '&:first-of-type': {
                paddingLeft: '12px',
              },
            },
            '&.colored-table': {
              padding: '2px',
              borderCollapse: 'separate',
              borderSpacing: '0 8px',
            },
            '& .colored-row': {
              position: 'relative',
              height: '74px',
            },

            '& .colored-row td': {
              backgroundColor: theme.palette.secondary.light,
            },
            '& .responsive-table-header': {
              display: 'none',
            },
            '& .splitter-cell': {
              width: '8px',
              minWidth: '8px',
              padding: 0,
              backgroundColor: `${theme.palette.background.default} !important`,
            },
            '& .link-cell': {
              position: 'absolute',
              top: 0,
              bottom: 0,
              right: 0,
              left: 0,
              borderRadius: 0,
              boxShadow: '0px 3px 3px theme.palette.secondary.dark',
            },
            '& .link-cell:focus': {
              backgroundColor: 'transparent !important',
            },
            '& .action-cell-header': {
              width: ACTION_CELL_WIDTH,
              textAlign: 'center',
              paddingRight: 0,
              paddingLeft: 0,
              whiteSpace: 'nowrap',
            },
            '& .action-cell': {
              width: ACTION_CELL_WIDTH,
              minWidth: ACTION_CELL_WIDTH,
              boxShadow: '0px 3px 3px theme.palette.secondary.dark',
            },
            /* styles for collapsing table to column on small screens */
            [theme.breakpoints.down('md')]: {
              '&.collapse-table thead': {
                display: 'none',
              },
              '&.collapse-table tr': {
                display: 'block',
                height: 'auto',
                marginBottom: '16px',
              },
              '&.collapse-table td': {
                display: 'block',
                padding: '8px',
              },
              '&.collapse-table .responsive-table-header': {
                display: 'inline-block',
                paddingRight: '4px',
              },
              '&.collapse-table .MuiTableCell-borderless': {
                display: 'none',
              },
              '&.collapse-table .splitter-cell': {
                display: 'none',
              },
              '&.collapse-table .action-cell': {
                display: 'inline-block',
              },
            },
          }),
        },
      },
      MuiTableCell: {
        styleOverrides: {
          root: ({ theme }: StyleOverrideRoot) => ({
            '&.MuiTableCell-head': {
              paddingTop: 0,
              paddingBottom: 0,
            },
            '&.sortable-table-header': {
              cursor: 'pointer',
              userSelect: 'none',
            },
            '&.MuiTableCell-borderless': {
              position: 'relative',
              paddingTop: 0,
            },
            // position an element on top of the border of the td above
            '&.MuiTableCell-borderless:before': {
              content: '""',
              width: '100%',
              height: 9,
              backgroundColor: theme.palette.secondary.light,
              top: -9,
              left: 0,
              position: 'absolute',
            },
            '&.MuiTableCell-root': {
              border: 0,
            },
            '&.MuiTableCell-infotable.MuiTableCell-body:not(.MuiTableCell-infotable-selected):hover:after': {
              content: '""',
              position: 'absolute',
              left: '0px',
              right: '0px',
              top: '-1000px',
              height: '100000px',
              backgroundColor: theme.palette.secondary.light,
              zIndex: -2,
            },
            '&.MuiTableCell-infotable': {
              position: 'relative',
              borderBottom: `1px solid ${theme.palette.secondary.main}`,
            },
            '&.MuiTableCell-infotable.MuiTableCell-body': {
              cursor: 'pointer',
            },
            '&.MuiTableCell-infotable-first': {
              borderTop: '3px solid transparent',
            },
            '&.MuiTableCell-infotable-first.MuiTableCell-infotable-selected': {
              borderTop: `3px solid ${theme.palette.primary.main}`,
            },
            '&.MuiTableCell-infotable-last.MuiTableCell-infotable-selected': {
              borderBottom: `3px solid ${theme.palette.primary.main}`,
            },
            '&.MuiTableCell-infotable-selected': {
              backgroundColor: theme.palette.secondary.light,
            },
            '&.MuiTableCell-infotable-selected:before, &.MuiTableCell-infotable-selected:after': {
              content: '""',
              position: 'absolute',
              top: -1,
              bottom: 0,
              width: 3,
              backgroundColor: theme.palette.primary.main,
            },
            '&.MuiTableCell-infotable-selected:before': {
              left: 0,
            },
            '&.MuiTableCell-infotable-selected:after': {
              right: 0,
            },
          }),
        },
      },
      MuiButtonBase: {
        defaultProps: {
          disableRipple: true,
        },
        styleOverrides: {
          root: {
            '&.MuiButtonBase-root.Mui-focusVisible': {
              boxShadow: '0px 0px 0px 3px #2264A1 inset',
              backgroundColor: '#f2f2f2',
              color: LightTheme.primary.main,
            },
            '&.MuiButton-containedPrimary:active': {
              backgroundColor: isDarkTheme ? '#69485F' : '#24021A',
            },
          },
        },
      },
      MuiTextField: {
        defaultProps: {
          InputLabelProps: { shrink: true },
        },
      },
      MuiSelect: {
        defaultProps: {
          notched: true,
        },
      },
      MuiInputLabel: {
        defaultProps: {
          shrink: true,
        },
      },
      MuiFormControlLabel: {
        styleOverrides: {
          root: ({ theme }: StyleOverrideRoot) => ({
            '&.MuiFormControlLabel-root.radio-connector': {
              margin: 0,
              flex: 1,
              alignItems: 'flex-start',
              position: 'relative',
            },
            '&.MuiFormControlLabel-root.radio-connector .MuiFormControlLabel-label': {
              width: 42,
              textAlign: 'center',
            },
            '&.MuiFormControlLabel-root.radio-connector:last-child': {
              flex: 0,
            },
            '&.MuiFormControlLabel-root.radio-connector:not(:last-child):after': {
              content: '""',
              top: '20px',
              position: 'absolute',
              right: '-13px',
              left: '30px',
              borderBottom: `2px solid ${theme.palette.primary.main}`,
              zIndex: 2,
            },
            [theme.breakpoints.down('md')]: {
              '&.MuiFormControlLabel-root.radio-connector .MuiFormControlLabel-label': {
                width: 64,
              },
              '&.radio-connector .MuiSvgIcon-root': {
                fontSize: 46,
              },
              '&.MuiFormControlLabel-root.radio-connector:not(:last-child):after': {
                top: '30px',
                borderBottomWidth: '4px',
                left: '49px',
              },
            },
          }),
        },
      },
      MuiRadio: {
        styleOverrides: {
          root: ({ theme }: StyleOverrideRoot) => ({
            '&.MuiRadio-root': {
              color: theme.palette.primary.main,
            },
          }),
        },
      },
      MuiSlider: {
        styleOverrides: {
          root: ({ theme }: StyleOverrideRoot) => ({
            '&.MuiSlider-root': {
              pointerEvents: navigator.maxTouchPoints > 0 ? 'none' : 'all', // disable clicks on slider track (only on mobile)
            },
            '& .MuiSlider-thumb': {
              pointerEvents: 'all', // enable dragging slider thumb
            },
            '& .MuiSlider-mark': {
              backgroundColor: theme.palette.primary.main,
              height: 8,
              width: 2,
            },
            '&.cupping-slider .MuiSlider-track': {
              border: 'none',
            },
            '&.cupping-slider .MuiSlider-rail': {
              opacity: 1,
            },
          }),
        },
      },
      MuiPaginationItem: {
        styleOverrides: {
          root: () => ({
            '&.MuiPaginationItem-root': {
              margin: 0,
            },
          }),
        },
      },
      MuiFormControl: {
        styleOverrides: {
          root: ({ theme }: StyleOverrideRoot) => ({
            // focus form field
            '&.MuiFormControl-root .Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: '#4780B5',
            },
            // idle form field
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: theme.palette.mode === 'dark' ? theme.palette.primary.main : '#BAB1B7',
            },
            // hover form field
            '& .MuiInputBase-root:not(.Mui-disabled):hover .MuiOutlinedInput-notchedOutline': {
              borderColor: theme.palette.primary.dark,
            },
          }),
        },
      },
      MuiToggleButton: {
        styleOverrides: {
          root: ({ theme }: StyleOverrideRoot) => ({
            '&.MuiToggleButton-primary.Mui-selected': {
              backgroundColor: theme.palette.primary.main,
              color: theme.palette.primary.contrastText,
            },
          }),
        },
      },
      MuiCheckbox: {
        styleOverrides: {
          root: ({ theme }: StyleOverrideRoot) => ({
            '&.MuiToggleButton-primary.Mui-selected': {
              backgroundColor: theme.palette.primary.main,
              color: theme.palette.primary.contrastText,
            },
          }),
        },
      },
    },
  }
}

const chartText = {
  light: '#E0E0E3',
  dark: '#565656',
  darker: '#303030',
  main: '#A0A0A3',
}

const HighchartsLightTheme: Highcharts.Options = {
  chart: {
    backgroundColor: LightTheme.background.default,
    style: {
      fontFamily: 'Roboto',
    },
  },
  tooltip: {
    backgroundColor: '#F4E6DD',
  },
}

const HighchartsDarkTheme: Highcharts.Options = {
  chart: {
    backgroundColor: DarkTheme.background.default,
    style: {
      fontFamily: 'Roboto',
    },
  },
  title: {
    style: {
      color: chartText.light,
    },
  },
  subtitle: {
    style: {
      color: chartText.light,
    },
  },
  xAxis: {
    labels: {
      style: {
        color: chartText.light,
      },
    },
    lineColor: chartText.darker,
    tickColor: chartText.dark,
    title: {
      style: {
        color: chartText.light,
      },
    },
  },
  yAxis: {
    gridLineColor: chartText.darker,
    labels: {
      style: {
        color: chartText.light,
      },
    },
    title: {
      style: {
        color: chartText.main,
      },
    },
  },
  tooltip: {
    backgroundColor: '#000000',
    style: {
      color: chartText.main,
    },
  },
  plotOptions: {
    series: {
      marker: {
        lineColor: '#333',
      },
    },
  },
  legend: {
    backgroundColor: '#000000',
    itemStyle: {
      color: chartText.light,
    },
    itemHoverStyle: {
      color: chartText.light,
    },
    itemHiddenStyle: {
      color: chartText.dark,
    },
  },
}

export const ThemeSettings = {
  highchartsDarkTheme: HighchartsDarkTheme,
  highchartsLightTheme: HighchartsLightTheme,
  darkTheme: DarkTheme,
  lightTheme: LightTheme,
  getStyleOverrides: getStyleOverrides,
}

declare module '@mui/material/styles' {
  interface Palette {
    et: Palette['primary']
    bt: Palette['primary']
    power: Palette['primary']
    fan: Palette['primary']
    rpm: Palette['primary']
    ror: Palette['primary']
    cracks: Palette['primary']
    target: Palette['primary']
    plotline: Palette['primary']
    tableHeader: Palette['primary']
    navbarHeader: Palette['primary']
    dryingStage: Palette['primary']
    maillardStage: Palette['primary']
    inletTemp: Palette['primary']
    drumTemp: Palette['primary']
    drumPressure: Palette['primary']
    bbp?: Palette['primary']
    qrcode: Palette['primary']
  }

  // allow configuration using `createTheme`
  interface PaletteOptions {
    et?: PaletteOptions['primary']
    bt?: PaletteOptions['primary']
    power?: PaletteOptions['primary']
    fan?: PaletteOptions['primary']
    rpm?: PaletteOptions['primary']
    ror?: PaletteOptions['primary']
    cracks?: PaletteOptions['primary']
    target?: PaletteOptions['primary']
    plotline?: PaletteOptions['primary']
    tableHeader?: PaletteOptions['primary']
    navbarHeader?: PaletteOptions['primary']
    dryingStage?: PaletteOptions['primary']
    maillardStage?: PaletteOptions['primary']
    inletTemp?: PaletteOptions['primary']
    drumTemp?: PaletteOptions['primary']
    drumPressure?: PaletteOptions['primary']
    bbp?: PaletteOptions['primary']
    qrcode?: PaletteOptions['primary']
  }

  interface PaletteColor {
    lighter?: string
    darker?: string
  }
}

// Update the Button's color prop options
declare module '@mui/material/Button' {
  interface ButtonPropsColorOverrides {
    et: true
    bt: true
    power: true
    inletTemp: true
  }
}

// Update the Chip's color prop options
declare module '@mui/material/Chip' {
  interface ChipPropsColorOverrides {
    et: true
    bt: true
    power: true
    inletTemp: true
    bbp: true
    rpm: true
    fan: true
  }
}
